import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { PlanService } from '../services/PlanService';
import { getPlanName } from '../config/paymentCatalog';
import { mapPlanLimits, resolvePlanConfig } from '../config/planConfigResolver';

/**
 * Hook para gerenciar limites de plano do usuário
 * 
 * @param {string} userId - ID do usuário
 * @returns {Object} Informações do plano e funções de verificação
 * 
 * @example
 * const { plano, limites, verificarLimite, podeUsar } = usePlanLimits(user.id);
 * 
 * // Verificar se pode criar propriedade
 * const resultado = await verificarLimite('propriedades');
 * if (!resultado.permitido) {
 *   // Mostrar modal de upgrade
 * }
 */
export function usePlanLimits(userId) {
  const [plano, setPlano] = useState(null);
  const [limites, setLimites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carrega informações do plano do usuário
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    async function fetchPlanInfo() {
      try {
        // Busca dados do usuário
        let { data: userData, error: userError } = await supabase
          .from('usuarios')
          .select('plano, plano_data_inicio, plano_data_expiracao, plano_periodicidade, plano_trial')
          .eq('id', userId)
          .single();

        if (userError) throw userError;

        // Verifica se o plano expirou e faz downgrade automático
        if (userData.plano !== 'starter' && userData.plano_data_expiracao) {
          const dataExpiracao = new Date(userData.plano_data_expiracao);
          if (dataExpiracao < new Date()) {
            await PlanService.verificarExpiracao(userId);
            // Re-busca os dados após o downgrade
            const { data: updatedUser } = await supabase
              .from('usuarios')
              .select('plano, plano_data_inicio, plano_data_expiracao, plano_periodicidade, plano_trial')
              .eq('id', userId)
              .single();
            if (updatedUser) userData = updatedUser;
          }
        }

        // Busca configuração do plano (usa plano já atualizado)
        let { data: planConfig, error: planError } = await supabase
          .from('planos_config')
          .select('*')
          .eq('plano', userData?.plano || 'starter')
          .maybeSingle();

        // O catálogo versionado evita tela quebrada e limites divergentes quando
        // a tabela ainda não recebeu a migração comercial mais recente.
        if (planError) planConfig = null;
        planConfig = resolvePlanConfig(userData?.plano || 'starter', planConfig);
        if (!planConfig) throw new Error('Configuração de plano indisponível');

        setPlano({
          tipo: userData.plano,
          dataInicio: userData.plano_data_inicio,
          dataExpiracao: userData.plano_data_expiracao,
          periodicidade: userData.plano_periodicidade,
          trial: userData.plano_trial,
          config: planConfig
        });

        setLimites(mapPlanLimits(planConfig));

        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar informações do plano:', err);
        setError(err.message);
        setLoading(false);
      }
    }

    fetchPlanInfo();
  }, [userId]);

  /**
   * Verifica se o usuário pode usar um recurso
   * @param {string} recurso - tipo do recurso (propriedades, talhoes, alertas, etc)
   * @returns {Promise<Object>} resultado com permitido, limite, usado, etc
   */
  const verificarLimite = useCallback(async (recurso) => {
    if (!userId) {
      return { permitido: false, error: 'Usuário não autenticado' };
    }

    try {
      const { data, error } = await supabase
        .rpc('verificar_limite_usuario', {
          p_user_id: userId,
          p_recurso: recurso
        });

      if (error) throw error;

      return data;
    } catch (err) {
      console.error('Erro ao verificar limite:', err);
      return { permitido: false, error: err.message };
    }
  }, [userId]);

  /**
   * Verifica de forma síncrona se uma feature está disponível no plano
   * @param {string} feature - nome da feature (relatorios, download, etc)
   * @returns {boolean}
   */
  const podeUsar = useCallback((feature) => {
    if (!limites) return false;

    const mapa = {
      'download_previsao': limites.downloadPrevisao,
      'relatorios_pdf': limites.relatoriosPdf,
      'relatorios_excel': limites.relatoriosExcel,
      'sensores_periodo': limites.sensoresPeriodoCompleto,
      'compartilhar': limites.compartilhamentosEnviar > 0 || limites.compartilhamentosEnviar === null
    };

    return mapa[feature] || false;
  }, [limites]);

  /**
   * Retorna o badge do plano atual
   */
  const getBadge = useCallback(() => {
    if (!plano?.config) return null;

    return {
      texto: plano.config.badge_texto,
      cor: plano.config.badge_cor,
      tipo: plano.tipo
    };
  }, [plano]);

  /**
   * Verifica se o plano está expirado
   */
  const isExpirado = useCallback(() => {
    if (!plano?.dataExpiracao) return false;
    return new Date(plano.dataExpiracao) < new Date();
  }, [plano]);

  /**
   * Retorna nome amigável do plano
   */
  const getNomePlano = useCallback(() => {
    return getPlanName(plano?.tipo || 'starter');
  }, [plano]);

  return {
    plano,
    limites,
    loading,
    error,
    verificarLimite,
    podeUsar,
    getBadge,
    isExpirado,
    getNomePlano,
    // Atalhos úteis
    isStarter: plano?.tipo === 'starter',
    isSensorFree: plano?.tipo === 'sensor_free',
    isPremium: plano?.tipo === 'premium',
    isPro: plano?.tipo === 'pro',
    isEssential: plano?.tipo === 'essential',
    isProfessional: plano?.tipo === 'professional',
    isManagement: plano?.tipo === 'management'
  };
}

export default usePlanLimits;
